import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/utils";
import SearchableSelect from "@/components/SearchableSelect";

const TYPE_LABELS = {
  purchase_request: "Purchase Request", purchase_order: "Purchase Order", rfq: "RFQ",
  outside_purchase: "Expense", card_transaction: "Ramp Transaction", vendor: "Vendor",
};
const TYPE_ORDER = ["purchase_request", "purchase_order", "rfq", "outside_purchase", "card_transaction", "vendor"];

const fmt = (n) => (n == null ? null : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2 })}`);

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [userId, setUserId] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [team, setTeam] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Global "/" opens and focuses search, from anywhere in the contractor portal.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "/" && !open && !["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setTimeout(() => inputRef.current?.focus(), 50);
    api.get("/contractor/team").then((r) => setTeam(r.data)).catch(() => {});
    api.get("/contractor/vendor-network").then((r) => setVendors(r.data)).catch(() => {});
  }, [open]);

  const hasFilters = userId || vendorId || dateFrom || dateTo;

  useEffect(() => {
    if (!open) return;
    if (q.trim().length < 2 && !hasFilters) {
      setData(null);
      return;
    }
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/contractor/search", {
        params: { q: q.trim(), user_id: userId || undefined, vendor_id: vendorId || undefined, date_from: dateFrom || undefined, date_to: dateTo || undefined },
      }).then((r) => setData(r.data)).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, userId, vendorId, dateFrom, dateTo, open]);

  const close = () => {
    setOpen(false);
    setQ(""); setUserId(""); setVendorId(""); setDateFrom(""); setDateTo(""); setData(null);
  };

  const firstResult = () => {
    if (!data) return null;
    for (const t of TYPE_ORDER) {
      if (data.results[t]?.length) return data.results[t][0];
    }
    return null;
  };

  const openResult = (r) => {
    navigate(r.url);
    close();
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") { close(); return; }
    if (e.key === "Enter") {
      const first = firstResult();
      if (first) openResult(first);
    }
  };

  const totalResults = data ? Object.values(data.totals).reduce((s, n) => s + n, 0) : 0;

  return (
    <>
      <button
        onClick={() => setOpen(true)} data-testid="open-search-button"
        aria-label="Search" title="Search (/)"
        className="w-9 h-9 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 transition-colors"
      >
        <Search size={17} />
      </button>

      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
        <DialogContent
          data-testid="global-search-overlay"
          onKeyDown={onKeyDown}
          className="max-w-2xl p-0 gap-0 top-[10%] translate-y-0"
        >
          <div className="flex items-center gap-2.5 px-4 h-14 border-b border-slate-100">
            <Search size={16} className="text-slate-400 shrink-0" />
            <input
              ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search purchase requests, POs, RFQs, expenses, transactions, vendors…"
              data-testid="global-search-input"
              className="flex-1 h-full outline-none text-[14px] bg-transparent pr-6"
            />
          </div>

          <div className="flex flex-wrap gap-2 px-4 py-2.5 border-b border-slate-100 bg-slate-50">
            <SearchableSelect value={userId} onChange={(e) => setUserId(e.target.value)} data-testid="search-user-filter" className="h-8 px-2 rounded-lg border border-slate-200 text-[12px] bg-white">
              <option value="">Any user</option>
              {team.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </SearchableSelect>
            <SearchableSelect value={vendorId} onChange={(e) => setVendorId(e.target.value)} data-testid="search-vendor-filter" className="h-8 px-2 rounded-lg border border-slate-200 text-[12px] bg-white">
              <option value="">Any vendor</option>
              {vendors.map((v) => <option key={v.vendor_id} value={v.vendor_id}>{v.vendor_name}</option>)}
            </SearchableSelect>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="search-date-from-filter" className="h-8 px-2 rounded-lg border border-slate-200 text-[12px] bg-white" />
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="search-date-to-filter" className="h-8 px-2 rounded-lg border border-slate-200 text-[12px] bg-white" />
            {hasFilters && (
              <button onClick={() => { setUserId(""); setVendorId(""); setDateFrom(""); setDateTo(""); }} data-testid="clear-search-filters" className="h-8 px-2 text-[12px] font-semibold text-sky-600">
                Clear filters
              </button>
            )}
          </div>

          <div className="max-h-[60vh] overflow-y-auto ss-scrollbar" data-testid="search-results">
            {q.trim().length < 2 && !hasFilters && (
              <div className="py-14 text-center text-slate-400 text-[13px]">Type at least 2 characters, or use a filter, to search.</div>
            )}
            {(q.trim().length >= 2 || hasFilters) && loading && !data && (
              <div className="py-14 text-center text-slate-400 text-[13px]">Searching…</div>
            )}
            {data && totalResults === 0 && (
              <div className="py-14 text-center text-slate-400 text-[13px]" data-testid="search-no-results">No results.</div>
            )}
            {data && TYPE_ORDER.filter((t) => data.results[t]?.length > 0).map((t) => (
              <div key={t} className="py-2" data-testid={`search-group-${t}`}>
                <div className="px-4 py-1 text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold">
                  {TYPE_LABELS[t]} {data.totals[t] > data.results[t].length ? `(showing ${data.results[t].length} of ${data.totals[t]})` : ""}
                </div>
                {data.results[t].map((r) => (
                  <button
                    key={r.id} onClick={() => openResult(r)} data-testid={`search-result-${t}-${r.id}`}
                    className="w-full flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-slate-50 text-left"
                  >
                    <div className="min-w-0">
                      <div className="text-[13px] font-medium text-slate-800 truncate">{r.title}</div>
                      <div className="text-[11.5px] text-slate-400 truncate">
                        {[r.subtitle, r.user, fmtDate(r.date, null)].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {fmt(r.amount) && <span className="mono text-[12.5px] text-slate-600">{fmt(r.amount)}</span>}
                      {r.status && <StatusBadge status={r.status} />}
                    </div>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
